import type {
  Observable,
  DeflateEncoderStreamId,
  Encoder,
  BufferedData,
  BufferedObservable,
  Telemetry,
  SessionManager,
} from '@datadog/browser-core'
import {
  sendToExtension,
  createPageMayExitObservable,
  canUseEventBridge,
  addTelemetryDebug,
  startAccountContext,
  startGlobalContext,
  startUserContext,
  startTabContext,
  ErrorSource,
  isExperimentalFeatureEnabled,
  ExperimentalFeature,
} from '@datadog/browser-core'
import { clocksNow } from '@datadog/js-core/time'
import { createDOMMutationObservable } from '../browser/domMutationObservable'
import { createWindowOpenObservable } from '../browser/windowOpenObservable'
import { startInternalContext } from '../domain/contexts/internalContext'
import { LifeCycle, LifeCycleEventType } from '../domain/lifeCycle'
import { startViewHistory } from '../domain/contexts/viewHistory'
import { startRequestCollection } from '../domain/requestCollection'
import { startWebSocketCollection } from '../domain/resource/webSocketCollection'
import { startActionCollection } from '../domain/action/actionCollection'
import { startErrorCollection } from '../domain/error/errorCollection'
import { startResourceCollection } from '../domain/resource/resourceCollection'
import { startViewCollection } from '../domain/view/viewCollection'
import { startRumBatch } from '../transport/startRumBatch'
import { startRumEventBridge } from '../transport/startRumEventBridge'
import { startUrlContexts } from '../domain/contexts/urlContexts'
import { createLocationChangeObservable } from '../browser/locationChangeObservable'
import type { RumConfiguration } from '../domain/configuration'
import type { ViewOptions } from '../domain/view/trackViews'
import { startFeatureFlagContexts } from '../domain/contexts/featureFlagContext'
import { startCustomerDataTelemetry } from '../domain/startCustomerDataTelemetry'
import { startPageStateHistory } from '../domain/contexts/pageStateHistory'
import { startDisplayContext } from '../domain/contexts/displayContext'
import { startVitalCollection } from '../domain/vital/vitalCollection'
import { startCiVisibilityContext } from '../domain/contexts/ciVisibilityContext'
import { startLongTaskCollection } from '../domain/longTask/longTaskCollection'
import { startSyntheticsContext } from '../domain/contexts/syntheticsContext'
import { startRumAssembly } from '../domain/assembly'
import { startSessionContext } from '../domain/contexts/sessionContext'
import { startConnectivityContext } from '../domain/contexts/connectivityContext'
import type { SdkName } from '../domain/contexts/defaultContext'
import { startDefaultContext } from '../domain/contexts/defaultContext'
import type { Hooks } from '../domain/hooks'
import { startEventCollection } from '../domain/event/eventCollection'
import { startSourceCodeMfeContext } from '../domain/contexts/sourceCodeMfeContext'
import type { RecorderApi, ProfilerApi } from './rumPublicApi'

export type StartRum = typeof startRum
export type StartRumResult = ReturnType<StartRum>

export function startRum(
  configuration: RumConfiguration,
  sessionManager: SessionManager,
  recorderApi: RecorderApi,
  profilerApi: ProfilerApi,
  initialViewOptions: ViewOptions | undefined,
  createEncoder: (streamId: DeflateEncoderStreamId) => Encoder,
  bufferedDataObservable: BufferedObservable<BufferedData>,
  telemetry: Telemetry,
  hooks: Hooks,
  sdkName?: SdkName
) {
  const cleanupTasks: Array<() => void> = []
  const lifeCycle = new LifeCycle()

  lifeCycle.subscribe(LifeCycleEventType.RUM_EVENT_COLLECTED, (event) => sendToExtension('rum', event))

  sessionManager.expireObservable.subscribe(() =>
    lifeCycle.notify(LifeCycleEventType.SESSION_EXPIRED, { endClocks: clocksNow() })
  )
  sessionManager.renewObservable.subscribe(() => lifeCycle.notify(LifeCycleEventType.SESSION_RENEWED))

  const reportError = (message: string) => {
    lifeCycle.notify(LifeCycleEventType.RAW_ERROR_COLLECTED, {
      error: { message, source: ErrorSource.AGENT, startClocks: clocksNow() },
    })
    // monitor-until: forever, to keep an eye on the errors reported to customers
    addTelemetryDebug('Error reported to customer', { 'error.message': message })
  }

  if (!canUseEventBridge()) {
    const batch = startRumBatch(configuration, lifeCycle, reportError, sessionManager.expireObservable, createEncoder)
    const preparePageExitSubscription = batch.prepareUrgentFlushObservable.subscribe((reason) => {
      lifeCycle.notify(LifeCycleEventType.PREPARE_URGENT_FLUSH, reason)
    })
    cleanupTasks.push(() => preparePageExitSubscription.unsubscribe())
    cleanupTasks.push(() => batch.stop())
    startCustomerDataTelemetry(telemetry, lifeCycle, batch.flushObservable)
  } else {
    startRumEventBridge(lifeCycle)
    const pageMayExitObservable = createPageMayExitObservable()
    const pageMayExitSubscription = pageMayExitObservable.subscribe((event) => {
      lifeCycle.notify(LifeCycleEventType.PREPARE_URGENT_FLUSH, event.reason)
    })
    cleanupTasks.push(() => pageMayExitSubscription.unsubscribe())
  }

  const { stop: stopRumEventCollection, ...startRumEventCollectionResult } = startRumEventCollection(
    lifeCycle,
    hooks,
    configuration,
    sessionManager,
    recorderApi,
    initialViewOptions,
    bufferedDataObservable,
    sdkName,
    reportError
  )
  cleanupTasks.push(stopRumEventCollection)
  bufferedDataObservable.unbuffer()

  // Add Clean-up tasks for Profiler API.
  cleanupTasks.push(() => profilerApi.stop())

  return {
    ...startRumEventCollectionResult,
    lifeCycle,
    sessionManager,
    stopSession: () => sessionManager.expire(),
    telemetry,
    stop: () => {
      cleanupTasks.forEach((task) => task())
    },
    hooks,
  }
}

export function startRumEventCollection(
  lifeCycle: LifeCycle,
  hooks: Hooks,
  configuration: RumConfiguration,
  sessionManager: SessionManager,
  recorderApi: RecorderApi,
  initialViewOptions: ViewOptions | undefined,
  bufferedDataObservable: Observable<BufferedData>,
  sdkName: SdkName | undefined,
  reportError: (message: string) => void
) {
  const cleanupTasks: Array<() => void> = []

  const domMutationObservable = createDOMMutationObservable()
  const locationChangeObservable = createLocationChangeObservable()
  const { observable: windowOpenObservable, stop: stopWindowOpen } = createWindowOpenObservable()
  cleanupTasks.push(stopWindowOpen)

  const { assemble: assembleHook } = hooks
  startDefaultContext(assembleHook, configuration, sdkName)
  const pageStateHistory = startPageStateHistory(assembleHook)
  cleanupTasks.push(() => pageStateHistory.stop())
  const viewHistory = startViewHistory(lifeCycle)
  cleanupTasks.push(() => viewHistory.stop())
  const urlContexts = startUrlContexts(lifeCycle, assembleHook, locationChangeObservable)
  cleanupTasks.push(() => urlContexts.stop())
  const featureFlagContexts = startFeatureFlagContexts(lifeCycle, assembleHook, configuration)
  startSessionContext(assembleHook, configuration, sessionManager, recorderApi, viewHistory)
  startConnectivityContext(assembleHook)
  startTabContext(assembleHook)
  const globalContext = startGlobalContext(assembleHook, configuration, 'rum', true)
  const userContext = startUserContext(assembleHook, configuration, sessionManager, 'rum')
  const accountContext = startAccountContext(assembleHook, configuration, 'rum')

  const actionCollection = startActionCollection(
    lifeCycle,
    hooks,
    domMutationObservable,
    windowOpenObservable,
    configuration
  )
  cleanupTasks.push(actionCollection.stop)

  const eventCollection = startEventCollection(lifeCycle)

  const displayContext = startDisplayContext(assembleHook)
  cleanupTasks.push(displayContext.stop)
  const ciVisibilityContext = startCiVisibilityContext(assembleHook)
  cleanupTasks.push(ciVisibilityContext.stop)
  startSyntheticsContext(assembleHook)

  startRumAssembly(configuration, lifeCycle, assembleHook, reportError)

  const {
    addTiming,
    setLoadingTime,
    startView,
    setViewName,
    setViewContext,
    setViewContextProperty,
    getViewContext,
    stop: stopViewCollection,
  } = startViewCollection(
    lifeCycle,
    hooks,
    configuration,
    domMutationObservable,
    windowOpenObservable,
    locationChangeObservable,
    recorderApi,
    viewHistory,
    initialViewOptions
  )

  startSourceCodeMfeContext(assembleHook)

  cleanupTasks.push(stopViewCollection)

  const resourceCollection = startResourceCollection(lifeCycle, configuration)
  cleanupTasks.push(resourceCollection.stop)

  const { stop: stopLongTaskCollection } = startLongTaskCollection(lifeCycle, configuration)
  cleanupTasks.push(stopLongTaskCollection)

  const { addError } = startErrorCollection(lifeCycle, bufferedDataObservable)

  startRequestCollection(lifeCycle, configuration, sessionManager, userContext, accountContext, bufferedDataObservable)

  const vitalCollection = startVitalCollection(lifeCycle, pageStateHistory)

  if (
    configuration.trackResources &&
    (configuration.betaTrackWebSockets || isExperimentalFeatureEnabled(ExperimentalFeature.TRACK_WEBSOCKETS))
  ) {
    const webSocketCollection = startWebSocketCollection(lifeCycle, viewHistory, vitalCollection.addDurationVital)
    cleanupTasks.push(webSocketCollection.stop)
  }

  const internalContext = startInternalContext(
    configuration.applicationId,
    sessionManager,
    viewHistory,
    actionCollection.actionContexts,
    urlContexts
  )

  return {
    addAction: actionCollection.addAction,
    startAction: actionCollection.startAction,
    stopAction: actionCollection.stopAction,
    startResource: resourceCollection.startResource,
    stopResource: resourceCollection.stopResource,
    addEvent: eventCollection.addEvent,
    addError,
    addTiming,
    setLoadingTime,
    addFeatureFlagEvaluation: featureFlagContexts.addFeatureFlagEvaluation,
    startView,
    setViewContext,
    setViewContextProperty,
    getViewContext,
    setViewName,
    viewHistory,
    sessionManager,
    stopSession: () => sessionManager.expire(),
    getInternalContext: internalContext.get,
    startDurationVital: vitalCollection.startDurationVital,
    stopDurationVital: vitalCollection.stopDurationVital,
    addDurationVital: vitalCollection.addDurationVital,
    addOperationStepVital: vitalCollection.addOperationStepVital,
    globalContext,
    userContext,
    accountContext,
    stop: () => cleanupTasks.forEach((task) => task()),
  }
}
